import React, { useState } from 'react';
import { Calendar, Clock, Phone, Video, MessageSquare, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Reveal, RevealGroup, RevealItem } from '../components/motion/Reveal';
import Seo from '../components/Seo';

const API = process.env.REACT_APP_BACKEND_URL;

const Consultation = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    consultationType: '',
    preferredDate: '',
    preferredTime: '',
    propertyLocation: '',
    consultationTopic: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.phone || !formData.consultationType) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API}/api/enquiries/consultation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.fullName,
          phone: formData.phone,
          email: formData.email || null,
          consultation_type: formData.consultationType,
          preferred_date: formData.preferredDate || null,
          preferred_time: formData.preferredTime || null,
          property_location: formData.propertyLocation || null,
          consultation_topic: formData.consultationTopic || null
        })
      });
      if (!res.ok) throw new Error('Submission failed');

      toast({
        title: "Consultation Booked!",
        description: "We'll confirm your appointment within 2 hours.",
      });

      setFormData({
        fullName: '',
        email: '',
        phone: '',
        consultationType: '',
        preferredDate: '',
        preferredTime: '',
        propertyLocation: '',
        consultationTopic: ''
      });
    } catch (err) {
      toast({
        title: "Booking Failed",
        description: "We couldn't submit your booking. Please try again or reach us on WhatsApp.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const consultationTypes = [
    { icon: Phone, title: "Phone Call", description: "15-30 minute phone consultation", duration: "Free" },
    { icon: Video, title: "Video Call", description: "30-minute video consultation", duration: "Free" },
    { icon: MessageSquare, title: "In-Person", description: "Office or site visit", duration: "₦20,000+" }
  ];

  const benefits = [
    "Expert guidance from licensed surveyors",
    "Property verification advice",
    "Understanding survey requirements",
    "Cost estimation for your project",
    "Legal compliance clarification",
    "Risk assessment and recommendations"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Book a Free Surveyor Consultation | Versatile Geopick"
        description="Get expert advice from licensed surveyors before you buy or build. Free phone and video consultations available across Lagos."
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-stone-100 via-amber-50/30 to-stone-50 pt-24">
        <Reveal className="text-center px-6" duration={0.9}>
          <Calendar className="h-16 w-16 text-emerald-700 mx-auto mb-6" />
          <h1 className="text-5xl md:text-7xl font-light text-stone-800 tracking-tight">
            Book a <span className="font-semibold text-emerald-700">Consultation</span>
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto mt-6">
            Get expert surveying advice before making property decisions
          </p>
        </Reveal>
      </section>

      {/* Why Consult */}
      <section className="py-16 bg-emerald-50 border-y border-emerald-200">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-light text-stone-800 mb-8">
            Why Consult with a Surveyor First?
          </h2>
          <p className="text-lg text-stone-700 leading-relaxed mb-8">
            Before purchasing land or starting construction, professional surveyor consultation can save you from costly mistakes, 
            legal disputes, and land fraud. Get expert guidance to protect your investment.
          </p>
          <RevealGroup className="grid md:grid-cols-3 gap-6">
            {consultationTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <RevealItem key={index}>
                <Card className="border h-full border-emerald-200 hover:border-emerald-400 transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-7 w-7 text-emerald-700" />
                    </div>
                    <h3 className="text-lg font-medium text-stone-800 mb-2">{type.title}</h3>
                    <p className="text-sm text-stone-600 mb-3">{type.description}</p>
                    <p className="text-emerald-700 font-semibold">{type.duration}</p>
                  </CardContent>
                </Card>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal direction="scale">
          <Card className="border-2 border-stone-200 shadow-xl">
            <CardContent className="p-10">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-light text-stone-800 mb-3">Schedule Your Consultation</h2>
                <p className="text-stone-600">
                  Fill in your details and we'll confirm your appointment
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                      className="border-stone-300 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+234 XXX XXX XXXX"
                      required
                      className="border-stone-300 focus:border-emerald-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className="border-stone-300 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Consultation Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="consultationType"
                    value={formData.consultationType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                  >
                    <option value="">Select consultation type</option>
                    <option value="phone">Phone Call (Free)</option>
                    <option value="video">Video Call (Free)</option>
                    <option value="office">In-Person (Office Visit)</option>
                    <option value="site">In-Person (Site Visit)</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Preferred Date
                    </label>
                    <Input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      className="border-stone-300 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Preferred Time
                    </label>
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                    >
                      <option value="">Select time</option>
                      <option value="09:00">9:00 AM</option>
                      <option value="10:00">10:00 AM</option>
                      <option value="11:00">11:00 AM</option>
                      <option value="12:00">12:00 PM</option>
                      <option value="14:00">2:00 PM</option>
                      <option value="15:00">3:00 PM</option>
                      <option value="16:00">4:00 PM</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Property Location
                  </label>
                  <Input
                    type="text"
                    name="propertyLocation"
                    value={formData.propertyLocation}
                    onChange={handleChange}
                    placeholder="Area/LGA in Lagos"
                    className="border-stone-300 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    What would you like to discuss?
                  </label>
                  <Textarea
                    name="consultationTopic"
                    value={formData.consultationTopic}
                    onChange={handleChange}
                    placeholder="Briefly describe your surveying needs or questions..."
                    rows={4}
                    className="border-stone-300 focus:border-emerald-500"
                  />
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
                  <p className="text-sm text-stone-700">
                    <strong className="text-emerald-700">Note:</strong> Free phone and video consultations are typically 15-30 minutes. 
                    In-person consultations may incur travel fees depending on location.
                  </p>
                </div>

                <Button
                  type="submit"
                  data-testid="consultation-submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-6 text-lg transition-all duration-300 disabled:opacity-60"
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  {isSubmitting ? 'Booking...' : 'Book Consultation'}
                </Button>
              </form>
            </CardContent>
          </Card>
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gradient-to-br from-stone-50 to-amber-50/20">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal className="text-center mb-12">
          <h2 className="text-3xl font-light text-stone-800">
            What You'll Get from Your Consultation
          </h2>
          </Reveal>
          <RevealGroup className="grid md:grid-cols-2 gap-6" stagger={0.08}>
            {benefits.map((benefit, index) => (
              <RevealItem key={index} direction="left" className="flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-emerald-700 flex-shrink-0 mt-0.5" />
                <p className="text-stone-700 text-lg">{benefit}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>
    </div>
  );
};

export default Consultation;
